import { skipToken } from "@reduxjs/toolkit/query";
import type { MultilineContent } from "@repo/app-types/content/types";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  CustomerInteractionSplitting,
  getCustomerInteractionSplitting,
  getOutcomeDefinitionFromPlaybook,
  getSummaryTemplateBodyFromPlaybook,
  PlaybookBranch,
  type PlaybookId,
  type PlaybookSettings,
  type PlaybookTree,
  type PlaybookUpdatePayload,
} from "@repo/app-types/entities/playbook";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { defaultAssessmentTree } from "@repo/app-types/helpers/recordingHelpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useAllRecordingAssessmentFeedbackForPlaybookQuery,
  useCreatePlaybookMutation,
  usePlaybookByIdQuery,
  usePublishPlaybookNodeMutation,
  useRevertPlaybookBranchMutation,
  useUpdatePlaybookMutation,
} from "../api/entities/playbook.js";

export interface PlaybookEditorData {
  name: string;
  tree: PlaybookTree;
  outcome: OutcomeDefinition | null;
  knowledgeBaseIds: readonly string[];
  gradingDataExtractionFieldIds: readonly string[];
  summaryTemplateBody: MultilineContent | null;
  customerInteractionSplitting: CustomerInteractionSplitting;
}

/** Describes which playbook fields have been changed locally */
export interface PlaybookChanges {
  nameChanged: boolean;
  treeChanged: boolean;
  outcomeChanged: boolean;
  knowledgeBasesChanged: boolean;
  dataExtractionChanged: boolean;
  summaryTemplateChanged: boolean;
  customerInteractionSplittingChanged: boolean;
}

export interface UsePlaybookEditorResult {
  /** The current playbook data (draft + local changes), undefined while loading */
  data: PlaybookEditorData | undefined;

  /** The draft playbook data (before local changes), undefined while loading */
  initialData: PlaybookEditorData | undefined;

  /** The live playbook data (for publish comparison), undefined while loading */
  liveData: PlaybookEditorData | undefined;

  /** Feedback data for the playbook (for UI display) */
  feedback: readonly FeedbackWithCurrentGrade[];

  /** True while fetching the playbook */
  isLoading: boolean;

  /** True while saving changes (autosave, publish, or revert) */
  isSaving: boolean;

  /** True if there are unsaved changes (vs draft revision) */
  hasUnsavedChanges: boolean;

  /** True if current data differs from live (anything to publish) */
  hasUnpublishedChanges: boolean;

  /** Describes which specific fields have changed locally (vs draft) */
  changes: PlaybookChanges;

  /** Describes which specific fields differ from live (for publish preview) */
  unpublishedChanges: PlaybookChanges;

  /** ISO timestamp of when the draft was last saved, null if no draft */
  lastSavedAt: string | null;

  /** The ID of the current draft revision, used to compare against test runs */
  draftRevisionId: string | null;

  /** Apply partial changes to the data */
  onChange: (changes: Partial<PlaybookEditorData>) => void;

  /** Discard all changes and revert draft to live (async) */
  onDiscardChanges: () => Promise<void>;

  /** Publish changes (promotes draft to live, or creates new playbook) */
  onPublishChanges: () => Promise<{ playbookId: PlaybookId } | undefined>;

  /** Publish one node of the local tree onto live. Local edits are kept, so the rest stays pending. */
  onPublishNode: (nodeId: string) => Promise<void>;

  /** Save changes to draft (used for autosave) */
  onSaveDraft: () => Promise<void>;
}

const DEFAULT_PLAYBOOK_DATA: PlaybookEditorData = {
  name: "Sales and compliance playbook",
  tree: defaultAssessmentTree,
  outcome: null,
  knowledgeBaseIds: [],
  gradingDataExtractionFieldIds: [],
  summaryTemplateBody: null,
  customerInteractionSplitting: CustomerInteractionSplitting.Never,
};

/**
 * Returns true if any field has changed.
 */
function computeHasAnyChanges(changes: PlaybookChanges): boolean {
  return (
    changes.nameChanged ||
    changes.treeChanged ||
    changes.outcomeChanged ||
    changes.knowledgeBasesChanged ||
    changes.dataExtractionChanged ||
    changes.summaryTemplateChanged ||
    changes.customerInteractionSplittingChanged
  );
}

/**
 * Computes which fields have changed between two playbook data objects.
 */
function computeChanges(
  current: PlaybookEditorData | undefined,
  baseline: PlaybookEditorData | undefined,
): PlaybookChanges {
  if (!baseline || !current) {
    return {
      nameChanged: false,
      treeChanged: false,
      outcomeChanged: false,
      knowledgeBasesChanged: false,
      dataExtractionChanged: false,
      summaryTemplateChanged: false,
      customerInteractionSplittingChanged: false,
    };
  }

  return {
    nameChanged: current.name !== baseline.name,
    treeChanged: JSON.stringify(current.tree) !== JSON.stringify(baseline.tree),
    outcomeChanged:
      JSON.stringify(current.outcome) !== JSON.stringify(baseline.outcome),
    knowledgeBasesChanged:
      JSON.stringify(current.knowledgeBaseIds.toSorted()) !==
      JSON.stringify(baseline.knowledgeBaseIds.toSorted()),
    dataExtractionChanged:
      JSON.stringify(current.gradingDataExtractionFieldIds.toSorted()) !==
      JSON.stringify(baseline.gradingDataExtractionFieldIds.toSorted()),
    summaryTemplateChanged:
      JSON.stringify(current.summaryTemplateBody) !==
      JSON.stringify(baseline.summaryTemplateBody),
    customerInteractionSplittingChanged:
      current.customerInteractionSplitting !==
      baseline.customerInteractionSplitting,
  };
}

/**
 * Converts an API playbook response to PlaybookEditorData.
 */
function toPlaybookEditorData(playbook: {
  name: string;
  assessment_tree: PlaybookTree;
  settings?: PlaybookSettings;
}): PlaybookEditorData {
  return {
    name: playbook.name,
    tree: playbook.assessment_tree,
    outcome: getOutcomeDefinitionFromPlaybook(playbook) ?? null,
    knowledgeBaseIds: playbook.settings?.knowledge_base?.ids ?? [],
    gradingDataExtractionFieldIds:
      playbook.settings?.grading_data_extraction?.custom_field_definition_ids ??
      [],
    summaryTemplateBody: getSummaryTemplateBodyFromPlaybook(playbook),
    customerInteractionSplitting: getCustomerInteractionSplitting(playbook),
  };
}

/** A save always sends the whole document. */
function toPlaybookUpdatePayload(
  data: PlaybookEditorData,
): Omit<PlaybookUpdatePayload, "parent_revision_id"> {
  return {
    name: data.name,
    assessment_tree: data.tree,
    knowledge_base_ids: [...data.knowledgeBaseIds],
    outcomes: data.outcome ? [data.outcome] : [],
    grading_data_extraction_field_ids: [...data.gradingDataExtractionFieldIds],
    summary_template_body: data.summaryTemplateBody,
    customer_interaction_splitting: data.customerInteractionSplitting,
  };
}

/**
 * Hook that manages the lifecycle and state of playbook editing.
 * Handles fetching, change tracking, discarding changes, and saving.
 *
 * For existing playbooks:
 * - Fetches both live and draft revisions
 * - Tracks local changes against the draft
 * - Supports autosave to draft and publish to live
 *
 * For new playbooks:
 * - Uses default data
 * - Only supports publish (creates new playbook)
 *
 * @param playbookId - The ID of the playbook to edit, or null for a new playbook
 */
export function usePlaybookEditor(
  playbookId: PlaybookId | null,
): UsePlaybookEditorResult {
  // Fetch live revision for existing playbooks
  const {
    data: fetchedLivePlaybook,
    isLoading: isLoadingLive,
    isFetching: isFetchingLive,
  } = usePlaybookByIdQuery(
    playbookId ? { id: playbookId, branch: PlaybookBranch.Live } : skipToken,
  );

  // Fetch draft revision for existing playbooks
  const {
    data: fetchedDraftPlaybook,
    isLoading: isLoadingDraft,
    isFetching: isFetchingDraft,
  } = usePlaybookByIdQuery(
    playbookId ? { id: playbookId, branch: PlaybookBranch.Draft } : skipToken,
  );

  // Fetch feedback for the playbook
  const { data: feedback } = useAllRecordingAssessmentFeedbackForPlaybookQuery(
    playbookId ?? skipToken,
  );

  // Mutations
  const [createPlaybook, { isLoading: isCreating }] =
    useCreatePlaybookMutation();
  const [updatePlaybook, { isLoading: isUpdating }] =
    useUpdatePlaybookMutation();
  const [publishPlaybookNode, { isLoading: isPublishingNode }] =
    usePublishPlaybookNodeMutation();
  const [revertPlaybookBranch, { isLoading: isReverting }] =
    useRevertPlaybookBranchMutation();

  // Track local changes as a partial object (undefined = no changes)
  const [changedData, setChangedData] = useState<Partial<PlaybookEditorData>>();

  // Track previous playbookId to reset changes in render phase when it changes
  const prevPlaybookIdRef = useRef(playbookId);
  if (prevPlaybookIdRef.current !== playbookId) {
    prevPlaybookIdRef.current = playbookId;
    setChangedData(undefined);
  }

  // Derive liveData from live revision
  const liveData: PlaybookEditorData | undefined = useMemo(() => {
    if (playbookId === null) {
      return DEFAULT_PLAYBOOK_DATA;
    }

    if (!fetchedLivePlaybook) {
      return undefined;
    }

    return toPlaybookEditorData(fetchedLivePlaybook);
  }, [playbookId, fetchedLivePlaybook]);

  // Derive initialData from draft revision (what we compare local changes against)
  const initialData: PlaybookEditorData | undefined = useMemo(() => {
    if (playbookId === null) {
      // New playbook - use defaults
      return DEFAULT_PLAYBOOK_DATA;
    }

    if (!fetchedDraftPlaybook) {
      // Still loading
      return undefined;
    }

    return toPlaybookEditorData(fetchedDraftPlaybook);
  }, [playbookId, fetchedDraftPlaybook]);

  // Compute current data by merging draft data with local changes
  const data: PlaybookEditorData | undefined = useMemo(() => {
    if (!initialData) {
      return undefined;
    }

    if (!changedData) {
      return initialData;
    }

    return {
      ...initialData,
      ...changedData,
    };
  }, [initialData, changedData]);

  // Calculate changes vs draft (for tracking unsaved changes)
  const changes: PlaybookChanges = useMemo(
    () => computeChanges(data, initialData),
    [data, initialData],
  );

  // Calculate unpublished changes (current data vs live)
  const unpublishedChanges: PlaybookChanges = useMemo(
    () => computeChanges(data, liveData),
    [data, liveData],
  );

  const hasUnsavedChanges = computeHasAnyChanges(changes);
  const hasUnpublishedChanges = computeHasAnyChanges(unpublishedChanges);

  // lastSavedAt and draftRevisionId from draft revision
  const lastSavedAt = fetchedDraftPlaybook?.revision_created_at ?? null;
  const draftRevisionId = fetchedDraftPlaybook?.revision_id ?? null;

  // onChange handler - merge partial changes
  const onChange = useCallback(
    (newChangedData: Partial<PlaybookEditorData>) => {
      setChangedData((prev) => ({
        ...prev,
        ...newChangedData,
      }));
    },
    [],
  );

  // onSaveDraft handler - save current data to draft branch
  // Uses useEffectEvent to capture current changedData via closure
  const onSaveDraft = useEffectEvent(async (): Promise<void> => {
    if (!data || !playbookId) {
      return;
    }

    await updatePlaybook({
      id: playbookId,
      branch: PlaybookBranch.Draft,
      ...toPlaybookUpdatePayload(data),
    }).unwrap();

    // Only clear local changes if user didn't make more changes during the save.
    // This prevents cursor jumping when user types during an in-flight save.
    // The changedData captured via useEffectEvent closure represents the state at save start.
    setChangedData((current) => {
      // If user made changes during the save, keep the new changes
      if (JSON.stringify(current) !== JSON.stringify(changedData)) {
        return current;
      }
      // Otherwise safe to clear - the data is now in the draft
      return undefined;
    });
  });

  // onDiscardChanges handler - revert draft to live and clear local changes
  const onDiscardChanges = useCallback(async (): Promise<void> => {
    // Clear local changes
    setChangedData(undefined);

    // Revert draft to live if this is an existing playbook
    if (playbookId) {
      await revertPlaybookBranch({
        id: playbookId,
        branch: PlaybookBranch.Draft,
      }).unwrap();
    }
  }, [playbookId, revertPlaybookBranch]);

  // onPublishChanges handler - save to live branch (or create new playbook)
  const onPublishChanges = useCallback(async (): Promise<
    { playbookId: PlaybookId } | undefined
  > => {
    if (!data) {
      return undefined;
    }

    if (playbookId === null) {
      // Create new playbook (goes straight to live)
      const result = await createPlaybook({
        name: data.name,
        assessment_tree: data.tree,
        knowledge_base_ids: [...data.knowledgeBaseIds],
        grading_data_extraction_field_ids: [
          ...data.gradingDataExtractionFieldIds,
        ],
        outcomes: data.outcome ? [data.outcome] : undefined,
        summary_template_body: data.summaryTemplateBody,
        customer_interaction_splitting: data.customerInteractionSplitting,
      }).unwrap();

      // Clear local changes on success
      setChangedData(undefined);

      return { playbookId: result.id };
    } else {
      // Update existing playbook on live branch
      const result = await updatePlaybook({
        id: playbookId,
        branch: PlaybookBranch.Live,
        ...toPlaybookUpdatePayload(data),
      }).unwrap();

      // Clear local changes on success
      setChangedData(undefined);

      return { playbookId: result.id };
    }
  }, [data, playbookId, createPlaybook, updatePlaybook]);

  // Sends the local tree; the caller flushes autosave first so the stored
  // draft matches it. Local changes are kept — the rest stays pending.
  const onPublishNode = useCallback(
    async (nodeId: string): Promise<void> => {
      if (playbookId === null || !data) {
        return;
      }

      await publishPlaybookNode({
        id: playbookId,
        node_id: nodeId,
        assessment_tree: data.tree,
      }).unwrap();
    },
    [data, playbookId, publishPlaybookNode],
  );

  // Determine loading state
  const isLoading =
    playbookId !== null &&
    (isLoadingLive || isFetchingLive || isLoadingDraft || isFetchingDraft);
  const isSaving = isCreating || isUpdating || isPublishingNode || isReverting;

  return {
    data,
    initialData,
    liveData,
    feedback: feedback ?? [],
    isLoading,
    isSaving,
    hasUnsavedChanges,
    hasUnpublishedChanges,
    changes,
    unpublishedChanges,
    lastSavedAt,
    draftRevisionId,
    onChange,
    onDiscardChanges,
    onPublishChanges,
    onPublishNode,
    onSaveDraft,
  };
}
